<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="building-card">
            <div class="card-header">
                <div class="left-section">
                    <h2>Floor Plan</h2>
                    <div v-if="showInfoCard" class="info-card">
                        <button class="close-btn" @click="closeInfoCard">×</button>
                        <span class="card-title">
                            <span style="font-weight: bold; color: gold;">
                                {{ activePathTitle }}
                            </span>
                        </span>
                    </div>
                </div>
                <div class="right-section">
                    <v-switch v-model="showPolygons" inset color="primary" hide-details density="compact"
                        class="hotspot-switch" label="Hotspots" />
                    <button @click="closeDialog" class="dialog-close-btn">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 6L18 18M6 18L18 6" stroke="white" stroke-width="2" stroke-linecap="round" />
                        </svg>
                    </button>
                </div>
            </div>

            <div class="building-container">
                <object id="svgObject" type="image/svg+xml" :data="svgPath" @load="onSvgLoad" class="scaled-svg" />
            </div>

            <div class="zoom-controls">
                <button @click="zoomIn" :class="{ selected: isZoomInActive }">＋</button>
                <button @click="zoomOut" :class="{ selected: isZoomOutActive }">－</button>
                <button @click="resetSvg" :class="{ selected: isResetActive }" class="reset-btn">
                    Reset
                </button>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, defineExpose } from 'vue';
import svgPanZoom from 'svg-pan-zoom';

const svgPath = ref('/assets/hbcFloorPlan3dMarked.svg');
const showPolygons = ref(true);
const showInfoCard = ref(false);
const activePathTitle = ref('');
const visible = ref(false);
const isLoading = ref(false);
const isZoomInActive = ref(false);
const isZoomOutActive = ref(false);
const isResetActive = ref(false);

let panZoomInstance = null;
let timeoutId = null;
let zoomableArea = null;
let svgElementListeners = []; // Track event listeners for cleanup
let touchStartTime = 0;
let touchCount = 0;

const openBuilding = () => {
    visible.value = true;
    isLoading.value = true;
    setTimeout(() => {
        isLoading.value = false;
        resetSvg();
    }, 500);
};

const closeDialog = () => {
    visible.value = false;
    showInfoCard.value = false;
    activePathTitle.value = '';
    cleanupSvgResources();
};

const cleanupSvgResources = () => {
    // Destroy panZoomInstance
    if (panZoomInstance) {
        panZoomInstance.destroy();
        panZoomInstance = null;
    }

    // Remove SVG event listeners
    const svgObject = document.getElementById('svgObject');
    const svgDoc = svgObject?.contentDocument;
    const svgElement = svgDoc?.querySelector('svg');
    if (svgElement) {
        svgElementListeners.forEach(({ element, type, listener }) => {
            element.removeEventListener(type, listener);
        });
        svgElementListeners = [];
    }

    // Clear zoomable area
    if (zoomableArea && svgElement && svgElement.contains(zoomableArea)) {
        try {
            svgElement.removeChild(zoomableArea);
        } catch (error) {
            console.warn('Failed to remove zoomableArea:', error.message);
        }
        zoomableArea = null;
    } else {
        zoomableArea = null; // Ensure zoomableArea is reset even if not removed
    }

    // Clear any pending timeouts
    if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }
};

const closeInfoCard = () => {
    showInfoCard.value = false;
    activePathTitle.value = '';
};

const zoomIn = () => {
    panZoomInstance?.zoomIn();
    isZoomInActive.value = true;
    timeoutId = setTimeout(() => (isZoomInActive.value = false), 300);
};

const zoomOut = () => {
    panZoomInstance?.zoomOut();
    isZoomOutActive.value = true;
    timeoutId = setTimeout(() => (isZoomOutActive.value = false), 300);
};

const resetSvg = () => {
    if (panZoomInstance) {
        panZoomInstance.resetZoom();
        panZoomInstance.fit();
        panZoomInstance.center();
        panZoomInstance.disablePan();
        isResetActive.value = true;
        timeoutId = setTimeout(() => (isResetActive.value = false), 300);
    }
};

const updatePathVisibility = (svgDoc) => {
    svgDoc.querySelectorAll('#Hotspots path').forEach((path) => {
        if (showPolygons.value) {
            const status = path.dataset.status || 'free';
            path.style.display = 'inline';
            path.style.fill = status === 'booked' ? '#FFCCCC' : '#CCFFCC';
            path.style.stroke = status === 'booked' ? '#FF9999' : '#99FF99';
            path.style.strokeWidth = '2';
            path.style.fillOpacity = '0.5';
        } else {
            path.style.display = 'none';
        }
    });
};

const highlightPath = (path) => {
    path.style.display = 'inline';
    path.style.stroke = '#FFD700';
    path.style.strokeWidth = '8';
    path.style.strokeOpacity = '1';
    timeoutId = setTimeout(() => {
        const status = path.dataset.status || 'free';
        if (showPolygons.value) {
            path.style.fill = status === 'booked' ? '#FFCCCC' : '#CCFFCC';
            path.style.stroke = status === 'booked' ? '#FF9999' : '#99FF99';
            path.style.strokeWidth = '2';
            path.style.fillOpacity = '0.5';
        } else {
            path.style.display = 'none';
        }
    }, 5000);
};

const screenToSvgCoordinates = (svgElement, x, y) => {
    try {
        const point = svgElement.createSVGPoint();
        point.x = x;
        point.y = y;
        return point.matrixTransform(svgElement.getScreenCTM().inverse());
    } catch (error) {
        console.warn('Failed to invert matrix:', error.message);
        return { x: 0, y: 0 };
    }
};

const isPointInZoomableArea = (x, y) => {
    if (!zoomableArea) return false;
    try {
        const { x: rectX, y: rectY, width, height } = zoomableArea.getBBox();
        return x >= rectX && x <= rectX + width && y >= rectY && y <= rectY + height;
    } catch (error) {
        console.warn('Error accessing zoomable area BBox:', error.message);
        return false;
    }
};

const onSvgLoad = () => {
    timeoutId = setTimeout(() => {
        const svgObject = document.getElementById('svgObject');
        const svgDoc = svgObject?.contentDocument;
        const svgElement = svgDoc?.querySelector('svg');
        if (!svgElement) {
            console.error('SVG element not found');
            return;
        }

        try {
            if (!svgElement.getAttribute('viewBox')) {
                svgElement.setAttribute('viewBox', '0 0 5333.3335 3333.3333');
            }

            zoomableArea = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
            zoomableArea.setAttribute('x', '1000');
            zoomableArea.setAttribute('y', '500');
            zoomableArea.setAttribute('width', '3333');
            zoomableArea.setAttribute('height', '2333');
            zoomableArea.setAttribute('fill', 'none');
            zoomableArea.setAttribute('pointer-events', 'all');
            svgElement.appendChild(zoomableArea);

            panZoomInstance = svgPanZoom(svgElement, {
                zoomEnabled: true,
                panEnabled: false,
                controlIconsEnabled: false,
                mouseWheelZoomEnabled: true,
                dblClickZoomEnabled: false,
                preventMouseEventsDefault: false,
                zoomScaleSensitivity: 0.2,
                fit: true,
                center: true,
                beforeZoom: (oldZoom, newZoom) => {
                    if (newZoom > 1 && !panZoomInstance.isPanEnabled()) {
                        panZoomInstance.enablePan();
                    } else if (newZoom <= 1 && panZoomInstance.isPanEnabled()) {
                        panZoomInstance.disablePan();
                    }
                    return true;
                },
            });

            const touchStartHandler = (e) => {
                touchStartTime = Date.now();
                touchCount = e.touches.length;

                if (e.target.tagName === 'svg' && e.target.parentNode.tagName === 'g') {
                    return;
                }

                if (e.touches.length === 1) {
                    e.preventDefault();
                    return;
                }

                if (e.touches.length >= 2) {
                    const touch1 = screenToSvgCoordinates(svgElement, e.touches[0].clientX, e.touches[0].clientY);
                    const touch2 = screenToSvgCoordinates(svgElement, e.touches[1].clientX, e.touches[1].clientY);

                    if (isPointInZoomableArea(touch1.x, touch1.y) && isPointInZoomableArea(touch2.x, touch2.y)) {
                        return;
                    } else {
                        e.preventDefault();
                    }
                }
            };

            const touchEndHandler = (e) => {
                if (touchCount === 1 && Date.now() - touchStartTime < 300) {
                    e.preventDefault();
                }
            };

            svgElement.addEventListener('touchstart', touchStartHandler);
            svgElement.addEventListener('touchend', touchEndHandler);
            svgElementListeners.push(
                { element: svgElement, type: 'touchstart', listener: touchStartHandler },
                { element: svgElement, type: 'touchend', listener: touchEndHandler }
            );

            const hotspotsGroup = svgDoc.querySelector('#Hotspots');
            if (!hotspotsGroup) {
                console.warn('Hotspots group not found in SVG');
                return;
            }

            const customLayout = {
                'Big Table': [300, 70, 260, 32, -20, 150],
                '2.02 Auditorium': [320, 70, 200, 30, 0, 0],
                'Toiletten Level 2 Townhall': [350, 90, -210, 28, -90, -50],
                '2.03 Factory Hammerbrooklyn Team': [680, 150, -150, 40, 0, 0],
                '2.06 FH Fixed Desks': [480, 150, -150, 40, 0, -200],
                'Toiletten Level 2 Auditorium': [390, 90, -240, 28, -90, -70],
                'Pantry Level 2': [260, 90, -180, 28, -90, -50],
                '2.05 acitoflux': [260, 90, -180, 28, -90, -50],
                '2.42 FH Team': [250, 90, -180, 28, -90, -50],
                '2.09 shades&contrast GmbH': [390, 90, -250, 28, -90, -50],
                '2.10 Meeting Room': [350, 90, -210, 28, -90, -70],
                '2.04 TikTok Creative Lab': [350, 90, -210, 28, -90, -70],
                '3D FloorPlan': [260, 60, -90, 26, 0, 0],
                '3D Floor Model': [280, 60, -90, 26, 0, 0],
                '3D Building': [300, 60, -90, 26, 0, 0],
            };

            hotspotsGroup.querySelectorAll('path').forEach((path, index) => {
                const title = path.querySelector('title')?.textContent?.trim() || `Hotspot ${index + 1}`;
                const status = title.includes('Toilet') || title.includes('Pantry') ? 'free' : (Math.random() > 0.5 ? 'booked' : 'free');
                path.dataset.status = status;

                const bbox = path.getBBox();
                const [width, height, offsetY, fontSize, rotation, offsetX] =
                    customLayout[title] || [280, 60, -80, 28, 0, 0];

                const btnX = bbox.x + bbox.width / 2 - width / 2 + offsetX;
                const btnY = bbox.y + offsetY;

                const g = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
                if (rotation !== 0) {
                    const centerX = btnX + width / 2;
                    const centerY = btnY + height / 2;
                    g.setAttribute('transform', `rotate(${rotation} ${centerX} ${centerY})`);
                }

                const svgBtn = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svgBtn.setAttribute('x', btnX);
                svgBtn.setAttribute('y', btnY);
                svgBtn.setAttribute('width', width);
                svgBtn.setAttribute('height', height);
                svgBtn.setAttribute('viewBox', `0 0 ${width} ${height}`);
                svgBtn.style.cursor = 'pointer';
                svgBtn.style.pointerEvents = 'all';
                svgBtn.style.touchAction = 'manipulation';

                const rect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
                rect.setAttribute('width', width);
                rect.setAttribute('height', height);
                rect.setAttribute('rx', '12');
                rect.setAttribute('fill', '#4a2a2a');
                rect.setAttribute('stroke', '#90ee90');
                rect.setAttribute('stroke-width', '2');
                rect.style.pointerEvents = 'all';

                const text = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'text');
                text.setAttribute('x', width / 2);
                text.setAttribute('y', height / 2 + fontSize / 3);
                text.setAttribute('font-size', fontSize);
                text.setAttribute('font-weight', 'bold');
                text.setAttribute('fill', '#fff');
                text.setAttribute('text-anchor', 'middle');
                text.textContent = title;
                text.style.pointerEvents = 'none';

                svgBtn.appendChild(rect);
                svgBtn.appendChild(text);
                g.appendChild(svgBtn);

                const handleClick = () => {
                    activePathTitle.value = title;
                    showInfoCard.value = true;
                    rect.setAttribute('stroke', '#FFD700');
                    rect.setAttribute('stroke-width', '4');
                    rect.setAttribute('filter', 'drop-shadow(0 0 10px #FFD700)');
                    highlightPath(path);
                    timeoutId = setTimeout(() => {
                        rect.setAttribute('stroke', '#90ee90');
                        rect.setAttribute('stroke-width', '2');
                        rect.removeAttribute('filter');
                    }, 5000);
                };

                svgBtn.addEventListener('click', handleClick);
                svgBtn.addEventListener('touchstart', (e) => {
                    e.preventDefault();
                    handleClick();
                });
                svgElementListeners.push(
                    { element: svgBtn, type: 'click', listener: handleClick },
                    {
                        element: svgBtn, type: 'touchstart', listener: (e) => {
                            e.preventDefault();
                            handleClick();
                        }
                    }
                );

                hotspotsGroup.appendChild(g);
                path.style.pointerEvents = 'none';
            });

            updatePathVisibility(svgDoc);
        } catch (error) {
            console.error('Error in onSvgLoad:', error.message);
            cleanupSvgResources();
        }
    }, 500);
};

const onResize = () => {
    const svgObject = document.getElementById('svgObject');
    const svgDoc = svgObject?.contentDocument;
    const svgElement = svgDoc?.querySelector('svg');
    if (!svgElement || !panZoomInstance) return;

    try {
        panZoomInstance.resize();
        panZoomInstance.fit();
        panZoomInstance.center();
        panZoomInstance.disablePan();
    } catch (error) {
        console.warn('Error during SVG resize:', error.message);
        resetSvg();
    }
};

watch(showPolygons, () => {
    const svgDoc = document.getElementById('svgObject')?.contentDocument;
    if (svgDoc) updatePathVisibility(svgDoc);
});

onMounted(() => {
    window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
    if (visible.value) {
        cleanupSvgResources();
    }
});

defineExpose({ openBuilding, resetSvg });
</script>

<style scoped>
.hotspot-switch {
    margin: 0;
    padding: 0;
    transform: scale(0.9);
    margin-right: 8px;
    margin-top: 2px;
}

.building-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
    padding: 2vh;
    width: 90vw;
    max-width: 1200px;
    height: 90vh;
    max-height: 800px;
    display: flex;
    flex-direction: column;
    position: relative;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1vh;
}

.left-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
}

.right-section {
    display: flex;
    margin-top: 0.5rem;
    align-items: flex-start;
    gap: 10px;
}

.card-header h2 {
    color: #2c3e50;
    margin: 0 0 1vh 0;
    font-size: 3vh;
}

.info-card {
    background: #2c3e50;
    border-radius: 8px;
    padding: 1vh 1.5vw;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: fit-content;
    max-width: 300px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    margin-bottom: 1vh;
    position: absolute;
    z-index: 10;
    top: 5vh;
    left: 0;
}

.card-title {
    color: #fff;
    font-size: 1.5vh;
    font-weight: bold;
    text-align: left;
}

.close-btn {
    background: none;
    border: none;
    color: #fff;
    font-size: 2vh;
    cursor: pointer;
    padding: 0 0.5vw;
    line-height: 1;
    position: absolute;
    top: 0.5vh;
    right: 0.5vw;
}

.close-btn:hover {
    color: #ff5555;
}

.dialog-close-btn {
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #4a2a2a;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.dialog-close-btn:hover {
    background-color: #6b3c3c;
    filter: drop-shadow(0 0 5px rgba(255, 100, 100, 0.5));
}

.building-container {
    flex-grow: 1;
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    touch-action: none;
}

.scaled-svg {
    width: 100%;
    height: 100%;
    border: none;
    touch-action: none;
}

.zoom-controls {
    display: flex;
    justify-content: center;
    gap: 2vw;
    padding: 1vh 0;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    margin-top: 1vh;
}

.zoom-controls button,
.zoom-controls .reset-btn {
    padding: 1.5vh 2vw;
    font-size: 2vh;
    font-weight: bold;
    background-color: #4a2a2a;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 15vw;
    touch-action: manipulation;
}

.zoom-controls button:hover,
.zoom-controls .reset-btn:hover {
    background-color: #6b3c3c;
    filter: drop-shadow(0 0 5px rgba(255, 100, 100, 0.5));
}

.zoom-controls button.selected,
.zoom-controls .reset-btn.selected {
    background-color: #90ee90;
    color: #222;
    filter: drop-shadow(0 0 5px rgba(144, 238, 144, 0.5));
}

.zoom-enter-active,
.zoom-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.zoom-enter-from,
.zoom-leave-to {
    transform: scale(0.7);
    opacity: 0;
}

.zoom-enter-to,
.zoom-leave-from {
    transform: scale(1);
    opacity: 1;
}

@media (orientation: landscape) {
    .building-card {
        width: 95vw;
        height: 85vh;
    }

    .card-header h2 {
        font-size: 2.5vh;
    }

    .info-card {
        width: 25vw;
        max-width: 200px;
        padding: 0.8vh 1vw;
        top: 4vh;
    }

    .card-title {
        font-size: 1.2vh;
    }

    .close-btn {
        font-size: 1.8vh;
    }

    .dialog-close-btn {
        padding: 6px;
    }

    .dialog-close-btn svg {
        width: 20px;
        height: 20px;
    }

    .zoom-controls button,
    .zoom-controls .reset-btn {
        padding: 1vh 1.5vw;
        font-size: 1.5vh;
        min-width: 10vw;
    }
}

@media (max-width: 480px) {
    .card-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .right-section {
        margin-top: 1vh;
        flex-wrap: wrap;
        gap: 5px;
    }

    .card-header h2 {
        font-size: 2vh;
    }

    .info-card {
        width: 20vw;
        max-width: 150px;
        padding: 0.5vh 0.8vw;
        top: 3vh;
    }

    .card-title {
        font-size: 1vh;
    }

    .close-btn {
        font-size: 1.5vh;
    }

    .dialog-close-btn {
        padding: 5px;
    }

    .dialog-close-btn svg {
        width: 18px;
        height: 18px;
    }

    .zoom-controls button,
    .zoom-controls .reset-btn {
        padding: 0.8vh 1.5vw;
        font-size: 1.2vh;
        min-width: 12vw;
    }
}
</style>